import { Controller } from "stimulus";

// Graphiques Chart.js 2.x aux couleurs du design system (maquette « chart ») :
// courbes orange/vertes avec aire legere et points blancs, barres vert clair,
// grilles discretes, legende en pastilles. Les donnees (labels, valeurs,
// couleur de courbe) viennent du serveur ; seul l'habillage est ici.
const FONT = "'Open Sans', system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";
const INK = "#121613";
const MUTED = "#6B726E";
const BODY = "#4D534F";
const GRID = "#E7EAE8";

const GREEN = "#127914";
const ORANGE = "#FF8C00";

// Vert, orange, sarcelle, bleu, or, brique, gris.
const PALETTE = [GREEN, ORANGE, "#2D8C86", "#2C6DB5", "#BE8A1F", "#B23A24", "#949B97"];

// "rgb(244, 140, 6)" ou "#127914" -> "rgba(r, g, b, alpha)".
function withAlpha(color, alpha) {
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color || "");
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;

  const hex = /^#([0-9a-f]{6})$/i.exec(color || "");
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  return color;
}

// Cles de semaine ISO (2026-10-05...) -> JJ/MM sur l'axe, JJ/MM/AAAA en infobulle.
function shortDate(label) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(label));
  return m ? `${m[3]}/${m[2]}` : label;
}

function longDate(label) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(label));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : label;
}

export default class extends Controller {
  static targets = ["content"];

  initialize() {
    Chart.defaults.global.defaultFontFamily = FONT;
    Chart.defaults.global.defaultFontColor = MUTED;
    Chart.defaults.global.defaultFontSize = 12;
  }

  connect() {
    const chartType = this.data.get("chartType");
    const keys = JSON.parse(this.data.get("keys"));
    const datasets = JSON.parse(this.data.get("datasets"));
    const colors = JSON.parse(this.data.get("colors"));

    const chartData = { labels: keys, datasets: [] };
    let options;

    if (chartType === "doughnut") {
      chartData.datasets = [{
        data: datasets[0].data,
        backgroundColor: colors || keys.map((_, i) => PALETTE[i % PALETTE.length]),
        borderColor: "#FFFFFF",
        borderWidth: 2,
        hoverBorderColor: "#FFFFFF",
        hoverOffset: 8
      }];
      options = this.doughnutOptions();
    } else if (chartType === "bar") {
      chartData.datasets = [{
        label: datasets[0].label,
        data: datasets[0].data,
        backgroundColor: withAlpha(GREEN, 0.38),
        hoverBackgroundColor: withAlpha(GREEN, 0.6),
        borderWidth: 0,
        barPercentage: 0.7,
        categoryPercentage: 0.8
      }];
      options = this.axisOptions();
    } else {
      chartData.datasets = datasets.map((dataset, i) => {
        const color = dataset.borderColor || PALETTE[(i + 1) % PALETTE.length];

        return Object.assign({}, dataset, {
          borderColor: color,
          backgroundColor: withAlpha(color, 0.1),
          fill: true,
          lineTension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          pointBackgroundColor: "#FFFFFF",
          pointBorderColor: color
        });
      });
      options = this.axisOptions();
    }

    this.chart = new Chart(this.contentTarget, {
      type: chartType,
      data: chartData,
      options: options
    });
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  tooltips(withDateTitle) {
    const tooltips = {
      backgroundColor: INK,
      titleFontFamily: FONT,
      titleFontStyle: "700",
      bodyFontFamily: FONT,
      bodyFontSize: 12,
      xPadding: 10,
      yPadding: 8,
      cornerRadius: 8,
      caretSize: 5
    };

    if (withDateTitle) {
      tooltips.callbacks = {
        title: (items) => (items.length ? longDate(items[0].label) : "")
      };
    }

    return tooltips;
  }

  legend(position, align) {
    return {
      display: true,
      position: position,
      align: align,
      labels: {
        usePointStyle: true,
        boxWidth: 8,
        padding: 16,
        fontColor: BODY,
        fontSize: 12,
        fontStyle: "600"
      }
    };
  }

  // Courbes et barres : axe X sans grille, axe Y a grille legere.
  axisOptions() {
    return {
      maintainAspectRatio: !this.data.has("height"),
      legend: this.legend("top", "end"),
      tooltips: this.tooltips(true),
      scales: {
        xAxes: [{
          gridLines: { display: false, drawBorder: false },
          ticks: {
            fontColor: MUTED,
            fontStyle: "600",
            maxRotation: 0,
            autoSkipPadding: 12,
            callback: (value) => shortDate(value)
          }
        }],
        yAxes: [{
          gridLines: { color: GRID, drawBorder: false, zeroLineColor: GRID },
          ticks: {
            beginAtZero: true,
            precision: 0,
            maxTicksLimit: 6,
            padding: 8,
            fontColor: MUTED
          }
        }]
      }
    };
  }

  doughnutOptions() {
    return {
      cutoutPercentage: 68,
      legend: this.legend("bottom", "center"),
      tooltips: this.tooltips(false)
    };
  }
}
